/**
 * Funnel tracker — fire-and-forget events to /api/analytics/funnel/event
 * so admins can see the registration drop-off curve in /admin/analytics.
 *
 * Anonymous: we only generate a client-side session UUID (persisted in
 * sessionStorage so it survives page reloads but resets on tab close).
 */
import { useCallback, useEffect, useRef } from 'react';

const SESSION_KEY = 'aga_funnel_session';

function getSessionId() {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = (crypto?.randomUUID && crypto.randomUUID()) || `s_${Math.random().toString(36).slice(2)}_${Date.now()}`;
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch (e) {
    console.debug('funnelTracker.getSessionId failed:', e?.message || e);
    return `s_${Math.random().toString(36).slice(2)}`;
  }
}

export function useFunnelTracker(tab = 'member') {
  const sentRef = useRef(new Set());
  const sessionId = useRef(getSessionId());

  const fire = useCallback((step, opts = {}) => {
    // Default to dedupe-per-session (e.g. field_focus:name fires once).
    const dedupe = opts.dedupe !== false;
    if (dedupe) {
      if (sentRef.current.has(step)) return;
      sentRef.current.add(step);
    }
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/analytics/funnel/event`;
    const body = JSON.stringify({
      session_id: sessionId.current,
      step,
      tab,
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    });
    // sendBeacon is more reliable on page-unload but unavailable in some
    // in-app browsers; fall back to fetch with keepalive.
    try {
      if (navigator.sendBeacon) {
        navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
        return;
      }
    } catch { /* fall through */ }
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, keepalive: true }).catch(() => {});
  }, [tab]);

  return fire;
}

/** Helper for components that want to fire `register_page_view` exactly once. */
export function usePageViewEvent(fire, eventName) {
  useEffect(() => {
    fire(eventName);
  }, [fire, eventName]);
}
